<template>
  <section :class="[bgColor]">
    <div
      class="rounded-tr-[2.5rem] px-6 lg:px-20"
      :class="[blockColor, verticalTopSpacing, verticalBottomSpacing]"
    >
      <div class="mx-auto max-w-[80rem]">
        <header class="mb-10">
          <h2 :class="[isDarkMode ? 'text-white' : 'text-text-dark']"
            v-html="title"
          >
          </h2>
          <p :class="[isDarkMode ? 'text-white' : 'text-text-dark']">
            {{ description }}
          </p>
        </header>
        <form
          class="max-w-[768px] rounded-md rounded-tl-none lg:bg-background-03 lg:p-10 lg:shadow-md"
          @submit.prevent="handleSubmit"
        >
          <div class="flex flex-col gap-6">
            <BaseRadio
              v-model="isCustomer"
              name="klant"
              :legend="props.translations.isCustomer"
              :options="customerOptions"
              :is-errored="!!errors.isCustomer"
              :error-text="errors.isCustomer"
              :dark-mode="isDarkMode"
            />

            <BaseInput
              v-model="loanNumber"
              :label="props.translations.loanNumber"
              has-label
              :placeholder="props.translations.loanNumberPlaceholder"
              :optional="props.translations.optional"
              :dark-mode="isDarkMode"
            />
            <BaseInput
              v-model="name"
              :label="props.translations.fullName"
              has-label
              :placeholder="props.translations.fullNamePlaceholder"
              :has-error="!!errors.name"
              :error-text="errors.name"
              :dark-mode="isDarkMode"
            />

            <BaseInput
              v-model="email"
              :label="props.translations.email"
              has-label
              :placeholder="props.translations.emailPlaceholder"
              :has-error="!!errors.email"
              :error-text="errors.email"
              :dark-mode="isDarkMode"
            />

            <BaseInput
              v-model="phone"
              :label="props.translations.phoneNumber"
              has-label
              :placeholder="props.translations.phoneNumberPlaceholder"
              :has-error="!!errors.phone"
              :error-text="errors.phone"
              :dark-mode="isDarkMode"
            />

            <BaseInput
              v-model="message"
              :label="props.translations.message"
              has-label
              :placeholder="props.translations.messagePlaceholder"
              type="textarea"
              :has-error="!!errors.message"
              :error-text="errors.message"
              :dark-mode="isDarkMode"
            />

            <BaseCheckbox
              id="agree"
              v-model="privacyAgreed"
              has-label
              :label="props.translations.privacyLabel"
              :value="props.translations.privacyStatement"
              :is-errored="!!errors.privacyAgreed"
              :error-text="errors.privacyAgreed"
              :dark-mode="isDarkMode"
            />

            <BaseButton
              variant="primary"
              size="small"
              type="submit"
              :is-disabled="!meta.valid || isSubmitting"
              :is-loading="isSubmitting"
              :text="props.translations.submit"
            >
            </BaseButton>
          </div>

          <p
            v-if="isSuccessSendBlockContactForm"
            role="alert"
            aria-live="polite"
            class="my-6 flex items-center gap-2 bg-background-05 px-6 py-4 text-text-dark"
          >
            <BaseIcon icon="Check" />
            Formulier succesvol verzonden
          </p>

          <p v-if="showGeneralError" role="alert" class="mt-4 text-states-error" aria-live="polite">
            {{ generalError }}
          </p>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup="setup" lang="ts">
import { computed, ref } from 'vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import { isValidPhone } from '@/common/utils/regex/isValidPhone.ts'
import BaseInput from '@/common/components/base/BaseInput/BaseInput.vue'
import BaseRadio from '@/common/components/base/BaseRadio/BaseRadio.vue'
import BaseCheckbox from '@/common/components/base/BaseCheckbox/BaseCheckbox.vue'
import BaseButton from '@/common/components/base/BaseButton/BaseButton.vue'
import BaseIcon from '@/common/components/base/BaseIcon/BaseIcon.vue'
import type { IBlockContactForm } from '@/features/block-contact-form/types/IBlockContactForm.ts'
import { getBgColor } from '@/common/utils/getBgColor.ts'
import useSendBlockContactForm from '@/features/block-contact-form/api/mutations/sendBlockContactForm.ts'

const props = defineProps<IBlockContactForm>()

const { sendBlockContactForm, isSuccessSendBlockContactForm } = useSendBlockContactForm()

const customerOptions = [
  { value: 'Ja', label: 'Ja' },
  { value: 'Nee', label: 'Nee' },
]

const bgColor = computed(() => getBgColor(props.background))

const blockColor = computed(() => {
  const blockColor = getBgColor(props.variant)
  return props.isDarkMode && props.hasBackgroundGradient
    ? 'bg-gradient-to-tr from-background-blue from-70% to-background-coral to-100%'
    : blockColor
})

//#region form
const schema = toTypedSchema(
  z.object({
    isCustomer: z.string().nonempty('Selecteer een optie'),
    name: z.string().nonempty('Naam is verplicht'),
    email: z.string().nonempty('E-mail is verplicht').email('Voer een geldig e-mailadres in'),
    phone: z
      .string()
      .nonempty('Telefoonnummer is verplicht')
      .refine(isValidPhone, 'Voer een geldig telefoonnummer in'),
    loanNumber: z.string().optional(),
    message: z.string().nonempty('Bericht is verplicht'),
    privacyAgreed: z.boolean().refine(val => val === true, {
      message: 'U moet akkoord gaan met de privacy statement',
    }),
  }),
)

const { defineField, errors, validate, values, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    isCustomer: 'Ja',
    privacyAgreed: false,
  },
})

const [isCustomer, isCustomerAttrs] = defineField('isCustomer')
const [name, nameAttrs] = defineField('name')
const [email, emailAttrs] = defineField('email')
const [phone, phoneAttrs] = defineField('phone')
const [message, messageAttrs] = defineField('message')
const [privacyAgreed, privacyAgreedAttrs] = defineField('privacyAgreed')
const [loanNumber, loonNumberAttrs] = defineField('loanNumber')

const handleSubmit = async () => {
  showGeneralError.value = false
  await validate()

  if (!meta.value.valid) {
    return
  }

  sendBlockContactForm(
    {
      fullName: values.name ?? '',
      email: values.email ?? '',
      phoneNumber: values.phone ?? '',
      message: values.message ?? '',
      isExistingCustomer: values.isCustomer === 'Ja',
      acceptPrivacyStatement: values.privacyAgreed ?? false,
      productId: props.productId ?? 0,
      fromEmail: props.email ?? '',
      loanNumber: values.loanNumber ?? '',
      pageKey: props.pageKey ?? '',
      blockKey: props.blockKey ?? '',
    },
    {
      onError() {
        showGeneralError.value = true
      },
    },
  )
}
//#endregion

// Form state
const isSubmitting = ref(false)
const showGeneralError = ref(false)
const generalError = ref(
  'Er is een fout opgetreden bij het verzenden van het formulier. Probeer het later opnieuw.',
)
</script>
